import { HugeiconsIcon } from "@hugeicons/react";
import {
  Call02Icon,
  Cancel01Icon,
  User02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import {
  UNKNOWN_CALLER_LABEL,
  isCallerUnknown,
  transferSourceLabel
} from "shared/utils/transferred-call";
import { formatPhoneDisplay } from "../views/phone-numbers/lib/format";
import { useSoftphone } from "./SoftphoneProvider";
import { SOFTPHONE_OVERLAY_Z_CLASS } from "./stacking";
import { useDraggableOverlay, type DragOffset } from "./useDraggableOverlay";

const ACTIVE_AVATAR_COLOR = "#29B6F6";
// Stage colors are partner-authored in CRM settings, so treat anything that is
// not a plain hex value as missing rather than feeding it to the style prop.
const HEX_COLOR = /^#[0-9a-f]{3,8}$/i;
const FALLBACK_STAGE_COLOR = "#8b8d97"; // content-300

interface IncomingCallScreenProps {
  // Collapse the popup to the incoming-call banner.
  onMinimize: () => void;
  // Drag position, owned by the overlay so it survives minimize/expand and the
  // hand-off to the in-call card once the call is answered (CRM-1318).
  dragOffset: DragOffset;
  onDragOffsetChange: (offset: DragOffset) => void;
}

/**
 * Incoming-call popup (CRM-905, Figma 23471:85906). Floats bottom-right like
 * the in-call card: caller avatar + number, matched lead name, the lead's
 * status (CRM-1221), "Incoming call" and the Start call / Decline pair.
 * Declining only takes THIS browser out of the call — colleagues keep ringing
 * (Jon, 2026-08-24) — hence "Decline" rather than "Cancel". ("Send to AI" from
 * the mockup is out of scope — no AI handoff in this ticket.) The X collapses
 * to the banner. Drag it anywhere on screen to uncover what it sits on top of
 * (CRM-1318): a ring for a colleague's line or a lead nobody is picking up
 * must not keep the Inbox Send button covered until it stops.
 *
 * A call the partner's AI leasing agent escalated (CRM-1393) shows the ORIGINAL
 * caller — the api already resolved them into the caller number — plus which
 * agent line they reached; when the api could not tell who was on the line the
 * headline says so instead of printing the park's own number as if it were a
 * person.
 */
export function IncomingCallScreen({
  onMinimize,
  dragOffset,
  onDragOffsetChange
}: IncomingCallScreenProps) {
  const { incomingCall, acceptIncoming, rejectIncoming } = useSoftphone();

  const { nodeRef, dragClassName, style, dragProps } = useDraggableOverlay({
    offset: dragOffset,
    onOffsetChange: onDragOffsetChange
  });

  if (!incomingCall) return null;

  const callerUnknown = isCallerUnknown(
    incomingCall.fromNumber,
    incomingCall.transferredFromNumber
  );
  const displayNumber = callerUnknown
    ? UNKNOWN_CALLER_LABEL
    : formatPhoneDisplay(incomingCall.fromNumber);
  const displayName = incomingCall.leadName?.trim();
  const displayStage = incomingCall.leadStage?.trim();
  const transferSource = transferSourceLabel(
    incomingCall.transferredFromNumber,
    incomingCall.transferredFromName
  );

  return (
    <div
      ref={nodeRef}
      role="dialog"
      aria-label="Incoming call"
      style={style}
      {...dragProps}
      className={cx(
        "fixed bottom-4 right-4 w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-alpha-100",
        dragClassName,
        SOFTPHONE_OVERLAY_Z_CLASS
      )}
    >
      <button
        type="button"
        aria-label="Minimize"
        onClick={onMinimize}
        className="absolute right-3 top-3 grid size-8 place-items-center rounded-full text-content-300 transition hover:bg-alpha-40 hover:text-content-1000"
      >
        <HugeiconsIcon icon={Cancel01Icon} size={20} />
      </button>

      <div className="flex flex-col items-center text-center">
        {/* Initials only for a matched lead; an unknown caller gets the
            silhouette (Jon, 2026-08-24) — the number must not go into `name`
            or it turns into "+(" initials. */}
        <Avatar
          name={displayName}
          size={72}
          className="text-2xl font-semibold"
          fallbackText={<HugeiconsIcon icon={User02Icon} size={32} />}
          fallbackBackgroundColor={ACTIVE_AVATAR_COLOR}
        />

        <p className="mt-4 text-base font-semibold text-content-1000">
          {displayNumber}
        </p>
        {displayName ? (
          <p className="mt-0.5 text-sm text-content-400">{displayName}</p>
        ) : null}
        {displayStage ? (
          <LeadStageChip
            name={displayStage}
            color={incomingCall.leadStageColor}
          />
        ) : null}
        {transferSource ? (
          <p className="mt-2 text-xs text-content-400">
            Transferred from {transferSource}
          </p>
        ) : null}
        {incomingCall.numberLabel ? (
          <p className="mt-0.5 text-xs text-content-300">
            To: {incomingCall.numberLabel}
          </p>
        ) : null}

        <div className="mt-8 min-h-12">
          <p className="text-lg font-semibold text-content-1000">
            Incoming call
          </p>
        </div>

        <div className="mt-6 flex items-start justify-center gap-10">
          <IncomingControlButton
            label="Start call"
            onClick={acceptIncoming}
            circleClassName="bg-success-500 text-white hover:bg-success-600"
            icon={Call02Icon}
          />
          <IncomingControlButton
            label="Decline"
            onClick={rejectIncoming}
            circleClassName="bg-danger-500 text-white hover:bg-danger-600"
            icon={Cancel01Icon}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * The matched lead's pipeline stage, shown the way the leads table shows it —
 * the stage's own color as a dot plus its name (CRM-1221). Only rendered when
 * the status is unambiguous; the backend omits it when the leads matching the
 * caller sit in different stages.
 */
function LeadStageChip({ name, color }: { name: string; color?: string }) {
  const dotColor =
    color && HEX_COLOR.test(color) ? color : FALLBACK_STAGE_COLOR;

  return (
    <span className="mt-2 inline-flex max-w-full items-center gap-1.5 rounded-full bg-alpha-40 px-2.5 py-1">
      <span
        aria-hidden="true"
        className="size-2 shrink-0 rounded-full"
        style={{ backgroundColor: dotColor }}
      />
      <span className="sr-only">Lead status:</span>
      <span className="min-w-0 truncate text-xs font-medium text-content-500">
        {name}
      </span>
    </span>
  );
}

function IncomingControlButton({
  label,
  onClick,
  circleClassName,
  icon
}: {
  label: string;
  onClick: () => void;
  circleClassName: string;
  icon: typeof Call02Icon;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-2"
    >
      <span
        className={cx(
          "grid size-12 place-items-center rounded-full transition",
          circleClassName
        )}
      >
        <HugeiconsIcon icon={icon} size={22} />
      </span>
      <span className="text-xs text-content-400">{label}</span>
    </button>
  );
}
